import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useForgotPasswordMutation, useVerifyResetCodeMutation } from '../store/api/authApi';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [forgotPassword, { isLoading, isSuccess }] = useForgotPasswordMutation();
  const [verifyCode, { isLoading: isVerifying }] = useVerifyResetCodeMutation();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [showCodeInput, setShowCodeInput] = useState(false);

  const validateEmail = (email: string): string | null => {
    if (!email.trim()) {
      return t('validation.email.required');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t('validation.email.invalid');
    }
    return null;
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    try {
      await forgotPassword({ email }).unwrap();
      setShowCodeInput(true);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setEmailError(error?.data?.message || error?.message || t('auth.forgotPassword.error'));
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodeError(null);

    if (!code.trim() || code.trim().length < 4) {
      setCodeError(t('auth.register.wrongCode'));
      return;
    }

    try {
      const result = await verifyCode({ email, code: code.trim() }).unwrap();
      navigate('/reset-password', { state: { verifiedToken: result.data.verifiedToken } });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      const msg = error?.data?.message || '';
      if (msg.toLowerCase().includes('too many')) {
        setShowCodeInput(false);
        setCodeError(msg);
      } else {
        setCodeError(msg || t('auth.register.wrongCode'));
      }
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setCodeError(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">
      <div className="w-full max-w-md animate-scale-in relative z-10">
        <div className="card-glass rounded-3xl shadow-2xl p-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-2 animate-slide-down">
            <h1 className="text-4xl font-heading font-bold text-brand-accent">
              {t('auth.forgotPassword.title')}
            </h1>
            <p className="text-neutral-medium font-body text-sm">
              {showCodeInput
                ? t('auth.forgotPassword.subtitleCode')
                : t('auth.forgotPassword.subtitle')}
            </p>
          </div>

          {/* Code Input */}
          {showCodeInput && (
            <div className="space-y-4 animate-slide-down">
              <p className="text-neutral-medium text-sm font-body text-center">
                {t('auth.register.codeSent')} {email}
              </p>
              <form onSubmit={(e) => { void handleVerifyCode(e); }} noValidate className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-body font-medium text-neutral-light text-center">
                    {t('auth.register.enterCode')}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={handleCodeChange}
                    className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light text-center text-2xl tracking-[0.5em] placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 font-body ${
                      codeError ? 'border-danger focus:ring-danger' : 'border-neutral-dark'
                    }`}
                    placeholder={t('auth.register.codePlaceholder')}
                    maxLength={6}
                  />
                  {codeError && (
                    <p className="text-danger text-xs font-body mt-1 text-center animate-slide-down">
                      {codeError}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isVerifying || code.length < 4}
                  className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isVerifying ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-brand-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('auth.register.verifying')}
                    </span>
                  ) : (
                    t('auth.register.verifyCode')
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Email Form */}
          {!showCodeInput && !isSuccess && (
            <form onSubmit={(e) => { void handleSendCode(e); }} noValidate className="space-y-5">
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-body font-medium text-neutral-light"
                >
                  {t('auth.login.email')}
                </label>
                <input
                  type="text"
                  id="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(null);
                  }}
                  className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 transform focus:scale-[1.02] font-body ${
                    emailError
                      ? 'border-danger focus:ring-danger focus:border-danger'
                      : 'border-neutral-dark focus:border-brand-accent'
                  }`}
                  placeholder="user@example.com"
                />
                {emailError && (
                  <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                    {emailError}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-brand-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('common.loading')}
                  </span>
                ) : (
                  t('auth.forgotPassword.submit')
                )}
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="text-center">
            <Link
              to="/login"
              className="text-sm text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-body"
            >
              {t('auth.forgotPassword.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
